import React from "react";
import { TableCell, TableRow } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AnalysisDriverType } from "../../types/types";
import colors from "assets/theme-dark/base/colors";
import { HeadroomFinalResponse } from "@/graphql/types/graphql";
import TruncatedText from "../TruncatedText";
import { formatMetric } from "../../utils/metricUtils";
import { HeadroomTableSegment } from "../../utils/headroomTableUtils";

interface RowSegment {
  range?: string;
  segmentValue?: string;
  data?: HeadroomFinalResponse;
  FxUniqueRowUuid?: string;
}

interface CombinedRow {
  manualRange?: string;
  dataValue?: string;
  data?: HeadroomFinalResponse;
}

interface HeadroomTableBodyRowsProps {
  /** Array of rows to render (either RowSegment[] or CombinedRow[]) */
  rows: RowSegment[][] | CombinedRow[];
  /** Table segments configuration */
  segments?: HeadroomTableSegment[];
  /** Mode for driver metrics */
  driverMode: AnalysisDriverType;
  /** Indicates if the segment is manual only */
  isManualOnly?: boolean;
  /** Indicates if the segment is data only */
  isDataOnly?: boolean;
  /** Indicates if there are two segments of the same method */
  isTwoSameMethod?: boolean;
  /** Indicates if there is one data and one manual segment */
  hasOneDataAndOneManualSegment?: boolean;
}

const HeadroomTableBodyRows: React.FC<HeadroomTableBodyRowsProps> = ({
  rows,
  segments,
  driverMode,
  isManualOnly,
  isDataOnly,
  isTwoSameMethod,
  hasOneDataAndOneManualSegment,
}) => {
  const renderSegmentCells = (row: RowSegment[] | CombinedRow, index: number) => {
    if (hasOneDataAndOneManualSegment) {
      const combinedRow = row as CombinedRow;
      return (
        <>
          <RegularTableCell align="center">{combinedRow.manualRange || ""}</RegularTableCell>
          <RegularTableCell align="center">
            <TruncatedText text={combinedRow.dataValue || ""} maxLength={20} />
          </RegularTableCell>
        </>
      );
    }

    const rowSegments = row as RowSegment[];
    if (isManualOnly || isDataOnly) {
      return (
        <>
          <RegularTableCell align="center">{isManualOnly ? "Manual" : "Data"}</RegularTableCell>
          <RegularTableCell align="center">
            <TruncatedText text={rowSegments[0].segmentValue || ""} maxLength={20} />
          </RegularTableCell>
        </>
      );
    }

    if (isTwoSameMethod && segments) {
      return rowSegments?.map((range, segIndex) => (
        <RegularTableCell key={`range-${segIndex}`} align="center" isEmpty={!range.range && !range.segmentValue}>
          <TruncatedText
            text={segments[segIndex].method === "DATA" ? range.segmentValue || "" : range.range || ""}
            maxLength={20}
          />
        </RegularTableCell>
      ));
    }

    return rowSegments?.map((range, segIndex) => (
      <React.Fragment key={`range-${segIndex}`}>
        <RegularTableCell align="center">{range.range || ""}</RegularTableCell>
        <RegularTableCell align="center">
          <TruncatedText text={range.segmentValue || ""} maxLength={20} />
        </RegularTableCell>
      </React.Fragment>
    ));
  };

  const renderMetricCells = (rangeData: HeadroomFinalResponse | undefined) => {
    if (!rangeData) {
      return (
        <>
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
          <RegularTableCell align="center" />
        </>
      );
    }

    return (
      <>
        <RegularTableCell align="center">{rangeData.totalCustomers ?? ""}</RegularTableCell>
        <RegularTableCell align="center">{rangeData.totalProspects ?? ""}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.penetration, "PERCENTAGE")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.driverInitial, "COLUMN")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.driverFinal, "COLUMN")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.headroom, "COLUMN")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.headroomPercent, "PERCENTAGE")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.increasedPenetration, "PERCENTAGE")}</RegularTableCell>
        <RegularTableCell align="center">
          {formatMetric(rangeData.totalCustomersAfter, "COLUMN", { noDecimals: true })}
        </RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.totalDriverInitial, "COLUMN")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.totalDriverFinal, "COLUMN")}</RegularTableCell>
        <RegularTableCell align="center">{formatMetric(rangeData.totalHeadroom, "COLUMN")}</RegularTableCell>
      </>
    );
  };

  return (
    <>
      {rows?.map((row, rowIndex) => {
        const rangeData: HeadroomFinalResponse | undefined = hasOneDataAndOneManualSegment
          ? (row as CombinedRow).data
          : (row as RowSegment[]).find((r) => r.data)?.data;
        const uuid = hasOneDataAndOneManualSegment
          ? (row as CombinedRow).data?.FxUniqueRowUuid ?? `row-${rowIndex}`
          : (row as RowSegment[]).find((r) => r.FxUniqueRowUuid)?.FxUniqueRowUuid ?? `row-${rowIndex}`;

        return (
          <TableRow key={uuid}>
            {renderSegmentCells(row, rowIndex)}
            {renderMetricCells(rangeData)}
          </TableRow>
        );
      })}
    </>
  );
};

const RegularTableCell = styled(TableCell)<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  color: colors.text.primary,
  backgroundColor: isEmpty ? colors.grey[100] : colors.background.default,
  whiteSpace: "nowrap",
}));

export default HeadroomTableBodyRows;
